// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
// AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
import interpolateMonetaryValue from './interpolateMonetaryValue'
import { buildRowLookup, isNegativeRow, rowKeyFor } from '../analysisHelpers/matrixRowLookup'
import mirrorScore from '../analysisHelpers/mirrorScore'
import toMillis from '../measurementSignals/toMillis'
import attributedValue from './applyRekenregels'
import resolveInterventionDose from './resolveInterventionDose'

/**
 * @typedef {object} MsiResponseRecord
 * @property {number | string} MomentPosition - Measure-moment position (enriched by fetchHierarchyData)
 * @property {string} RowTitle - Matrix row title (= MSI question)
 * @property {string} Input - Chosen score as string, "1".."5"
 * @property {string} Persona - Persona ID when the response is persona-coupled, '' otherwise
 * @property {string} FormID - Submission session id (unique per submission)
 * @property {*} [Timestamp] - Moment van de inzending; de laatste is het eind van een traject zonder einddatum
 */

/**
 * @typedef {object} MsiPersonaDelta
 * @property {string} persona - Persona ID
 * @property {number} firstAvg - Average (flipped) score at the persona's first moment
 * @property {number} lastAvg - Average (flipped) score at the persona's last moment
 * @property {number} delta - € value created: amount(lastLevel) − amount(firstLevel)
 * @property {number} firstLevel - Niveau 1–5 van de eerste geldige meting (half naar boven afgerond)
 * @property {number} lastLevel - Niveau 1–5 van de laatste geldige meting
 * @property {number|null} horizon - Tijdshorizon in jaren (rekenregels), null = onbekend
 * @property {number|null} attributed - Toegerekende waarde: delta × impactclaim × factor; null = niet doorgerekend
 * @property {string|null} doseReason - Waarom er geen horizon is
 * @property {{ months: string|null, hours: string|null }} doseSource - Herkomst van duur en uren
 */

/**
 * @typedef {object} MsiAnonymousDelta
 * @property {number} respondentCount - Unique respondents (FormID) at the last moment
 * @property {number} firstAvg - Anonymous average score at the first moment
 * @property {number} lastAvg - Anonymous average score at the last moment
 * @property {number} delta - € value created: (gemiddeld bedrag laatste − gemiddeld bedrag eerste moment) × respondentCount
 * @property {Array<number>} firstLevels - Niveau per respondent op het eerste moment
 * @property {Array<number>} lastLevels - Niveau per respondent op het laatste moment
 * @property {number|null} horizon - Tijdshorizon uit de standaard van de organisatie
 * @property {number|null} attributed - Toegerekende waarde, null = niet doorgerekend
 * @property {string|null} doseReason - Waarom er geen horizon is
 */

/**
 * @typedef {object} MsiEffectValue
 * @property {boolean} noScores - True when the effect has no usable monetary scores (contributes €0, visibly)
 * @property {number} momentCount - Distinct measured moments in the usable records
 * @property {number} deltaTotal - Sum of persona deltas + anonymous delta (0 when fewer than 2 moments)
 * @property {Array<MsiPersonaDelta>} perPersona - Personas measured at ≥2 moments
 * @property {MsiAnonymousDelta | null} anonymous - Anonymous aggregate, null without ≥2 anonymous moments
 * @property {number | null} stateValue - Last-moment situation value: interp(avg) × unique respondents
 * @property {number | null} firstAvg - Overall average (flipped) score at the first measured moment — the prognose baseline
 * @property {number} lastRespondents - Unique respondents at the last measured moment
 * @property {number} attributedTotal - Som van de toegerekende waarden die doorgerekend konden worden
 * @property {number} notAttributedCount - Deelnemers + anonieme respondenten waarvan het verschil niet doorgerekend kon worden
 * @property {Object<string, number>} notAttributedReasons - Dezelfde telling per reden
 * @property {number} incompleteCount - Metingen (persoon × moment) die niet telden omdat er te veel stellingen ontbraken
 */

// Average of the (flipped) numeric values per moment position → { pos: avg }
// Kennisbank, besluit 2026-10-26 (rekenregels-monetarisering.md 'Van
// antwoorden naar niveau', statistische-toetsing.md Stap 1): per persoon en per
// meting het gemiddelde van de ingevulde stellingen, eerst op twee decimalen
// tegen drijvendekommaruis (3,4999999 → 3,50), dan half naar boven op een heel
// niveau: floor(gem + 0,5). Niet Math.round op de onafgeronde waarde.
const levelOf = (avg, lowerScale, upperScale) => {
    const twoDecimals = Math.round(avg * 100) / 100
    return Math.min(upperScale, Math.max(lowerScale, Math.floor(twoDecimals + 0.5)))
}

// Een meting is geldig als hooguit één stelling ontbreekt; bij drie of minder
// stellingen moeten ze alle drie ingevuld zijn (statistische-toetsing.md Stap 1.4).
// Zonder bekende stellingen (geen rijdefinities) is er niets te toetsen.
const isComplete = (expected, answered) => {
    if (!expected) return true
    const missing = Math.max(0, expected - answered)
    return expected <= 3 ? missing === 0 : missing <= 1
}

const sortedPositions = (byPos) => Object.keys(byPos).map(Number).sort((a, b) => a - b)

const mean = (values) => values.reduce((sum, v) => sum + v, 0) / values.length

/**
 * Turns the response records of ONE MSI effect into euro values using the
 * meetstandaard-API `scores[]` of that effect. Value model: delta between the
 * first and last measured moment (yearly situation values, so impact = change).
 * Each measurement (person × moment) gets a whole level 1–5 and the amount of
 * that level (kennisbank 2026-10-26); incomplete measurements don't count.
 * Persona-coupled responses get a per-persona delta; anonymous responses can't
 * be paired across moments (FormID is unique per submission), so they get an
 * aggregate delta × respondent count. Respondents are deduped by FormID —
 * matrix questions store one doc per row per respondent.
 *
 * @param {object} params
 * @param {Array<MsiResponseRecord>} params.records - Response records as returned by fetchHierarchyData
 * @param {Array<{ Title: string, posNeg?: string }>} params.rowDefs - Raw MatrixQuestionRows defs (NOT the collapsed 'Gemiddelde' set)
 * @param {number} params.lowerScale - Scale lower bound (ReachStart, 1 for MSI)
 * @param {number} params.upperScale - Scale upper bound (ReachEnd, 5 for MSI)
 * @param {Array<{ score: number, monetaryValue: number }>} params.scores - meetstandaard-API `scores[]` for this effect
 * @param {Object<string, object>} [params.personasById] - Personas-docs op ID, voor de Traject-velden (rekenregels)
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [params.orgDefault] - CompagnyMeta.DefaultInterventionDose
 * @returns {MsiEffectValue}
 */
const computeMsiEffectValue = ({ records, rowDefs, lowerScale = 1, upperScale = 5, scores, personasById = {}, orgDefault = null } = {}) => {
    const noScores = interpolateMonetaryValue(lowerScale, scores) === null

    // Valence via RowID; the frozen RowTitle is only the legacy fallback, so a
    // reworded question keeps producing the same euros.
    const rowLookup = buildRowLookup(rowDefs)

    // Usable records: numeric non-empty Input, known moment position; flip negative rows
    const usable = []
    ;(Array.isArray(records) ? records : []).forEach((r) => {
        if (r?.Input == null || r.Input === '' || (!r.RowTitle && !r.RowID)) return
        const num = Number(r.Input)
        if (!Number.isFinite(num)) return
        const pos = typeof r.MomentPosition === 'number' ? r.MomentPosition : parseInt(r.MomentPosition, 10)
        if (!Number.isFinite(pos)) return
        usable.push({
            pos,
            value: mirrorScore(num, { lowerScale, upperScale }, isNegativeRow(rowLookup, r)),
            // Legacy garbage: ?persona=undefined in collection URLs stamped the
            // STRING "undefined" — those respondents are anonymous, not one
            // shared deelnemer.
            persona: r.Persona && r.Persona !== 'undefined' ? r.Persona : '',
            // Inzending: FormID, anders SessionID, anders het tijdstip — twee
            // kenmerkloze inzendingen mogen niet samenvallen (countSubmissions).
            formId: r.FormID || r.SessionID || `ms:${toMillis(r.Timestamp) ?? ''}`,
            ms: toMillis(r.Timestamp),
            fieldId: r.FieldID || '',
            rowKey: rowKeyFor(rowLookup, r),
            known: Boolean(rowLookup && (rowLookup.byId[r.RowID] || rowLookup.byTitle[r.RowTitle])),
        })
    })

    const momentCount = new Set(usable.map((r) => r.pos)).size

    if (noScores || usable.length === 0) {
        return {
            noScores, momentCount, deltaTotal: 0, perPersona: [], anonymous: null, stateValue: null, firstAvg: null, lastRespondents: 0,
            attributedTotal: 0, notAttributedCount: 0, notAttributedReasons: {}, incompleteCount: 0,
        }
    }

    // --- Metingen: één persoon (of één anonieme inzending) op één moment ---
    // Stellingen tellen op hun sleutel: dezelfde rij kan in meer dan één
    // versie in de definities staan en is dan toch één stelling.
    const rowsPerField = {}
    const allRowKeys = new Set()
    ;(Array.isArray(rowDefs) ? rowDefs : []).forEach((d) => {
        const key = d && (d.ID || d.Title)
        if (!key) return
        allRowKeys.add(key)
        if (d.Field) (rowsPerField[d.Field] ||= new Set()).add(key)
    })
    const allRows = allRowKeys.size
    const units = new Map()
    usable.forEach((r) => {
        const who = r.persona || `form:${r.formId}`
        const key = `${who}|${r.pos}|${r.fieldId}`
        if (!units.has(key)) units.set(key, { who, persona: r.persona, pos: r.pos, fieldId: r.fieldId, rows: new Map(), ms: null })
        const unit = units.get(key)
        // Twee invullingen op één moment: per stelling telt de laatste.
        const prev = unit.rows.get(r.rowKey)
        if (!prev || (r.ms ?? -Infinity) >= (prev.ms ?? -Infinity)) unit.rows.set(r.rowKey, r)
        if (r.ms !== null && (unit.ms === null || r.ms > unit.ms)) unit.ms = r.ms
    })
    let incompleteCount = 0
    const measurements = []
    units.forEach((unit) => {
        const rows = [...unit.rows.values()]
        const expected = (unit.fieldId && rowsPerField[unit.fieldId] && rowsPerField[unit.fieldId].size) || allRows
        const answered = rows.filter((r) => r.known).length
        if (!isComplete(expected, answered)) {
            incompleteCount += 1
            return
        }
        const avg = mean(rows.map((r) => r.value))
        const level = levelOf(avg, lowerScale, upperScale)
        measurements.push({ ...unit, avg, level, amount: interpolateMonetaryValue(level, scores) })
    })
    // Per persoon of inzending één meting per moment: bij meerdere velden de laatste.
    const perWho = new Map()
    measurements.forEach((m) => {
        const key = `${m.who}|${m.pos}`
        const prev = perWho.get(key)
        if (!prev || (m.ms ?? -Infinity) >= (prev.ms ?? -Infinity)) perWho.set(key, m)
    })
    const valid = [...perWho.values()]

    // --- Persona-coupled: delta per persona, needs ≥2 geldige metingen ---
    const byPersona = {}
    valid.filter((m) => m.persona).forEach((m) => {
        ;(byPersona[m.persona] ||= {})[m.pos] = m
    })

    const perPersona = []
    Object.keys(byPersona).forEach((persona) => {
        const positions = sortedPositions(byPersona[persona])
        if (positions.length < 2) return
        const first = byPersona[persona][positions[0]]
        const last = byPersona[persona][positions[positions.length - 1]]
        const delta = last.amount - first.amount
        // Eind van een traject zonder einddatum = de laatste meting in dit verschil (besluit R3)
        const dose = resolveInterventionDose({ persona: personasById[persona] || null, lastMeasurementMs: last.ms, orgDefault })
        perPersona.push({
            persona,
            firstAvg: first.avg,
            lastAvg: last.avg,
            firstLevel: first.level,
            lastLevel: last.level,
            delta,
            horizon: dose.horizon,
            attributed: attributedValue(delta, dose.horizon),
            doseReason: dose.reason,
            doseSource: dose.source,
        })
    })

    // --- Anonymous: per inzending een niveau; gemiddeld bedrag × respondenten op het laatste moment ---
    const anonByPos = {}
    valid.filter((m) => !m.persona).forEach((m) => {
        ;(anonByPos[m.pos] ||= []).push(m)
    })
    const anonPositions = sortedPositions(anonByPos)
    let anonymous = null
    if (anonPositions.length >= 2) {
        const first = anonByPos[anonPositions[0]]
        const last = anonByPos[anonPositions[anonPositions.length - 1]]
        const respondentCount = last.length
        const delta = (mean(last.map((m) => m.amount)) - mean(first.map((m) => m.amount))) * respondentCount
        // Anoniem is er geen traject: alleen de standaard van de organisatie
        const dose = resolveInterventionDose({ persona: null, orgDefault })
        anonymous = {
            respondentCount,
            firstAvg: mean(first.map((m) => m.avg)),
            lastAvg: mean(last.map((m) => m.avg)),
            firstLevels: first.map((m) => m.level),
            lastLevels: last.map((m) => m.level),
            delta,
            horizon: dose.horizon,
            attributed: attributedValue(delta, dose.horizon),
            doseReason: dose.reason,
        }
    }

    const deltaTotal = perPersona.reduce((sum, p) => sum + p.delta, 0) + (anonymous ? anonymous.delta : 0)

    // Toegerekende waarde: alleen wat doorgerekend kon worden telt op; de rest
    // wordt per persoon geteld, nooit als 0 meegeteld (besluit R2).
    let attributedTotal = 0
    let notAttributedCount = 0
    const notAttributedReasons = {}
    const tally = (entry, people) => {
        if (entry.attributed !== null) {
            attributedTotal += entry.attributed
            return
        }
        notAttributedCount += people
        notAttributedReasons[entry.doseReason] = (notAttributedReasons[entry.doseReason] || 0) + people
    }
    perPersona.forEach((p) => tally(p, 1))
    if (anonymous) tally(anonymous, anonymous.respondentCount)

    // --- Situatiewaarde: som van de bedragen op het laatste geldige moment ---
    const allByPos = {}
    valid.forEach((m) => { (allByPos[m.pos] ||= []).push(m) })
    const allPositions = sortedPositions(allByPos)
    const lastAll = allPositions.length ? allByPos[allPositions[allPositions.length - 1]] : []
    const lastRespondents = lastAll.length
    const stateValue = lastAll.length ? lastAll.reduce((sum, m) => sum + m.amount, 0) : null

    // Gemiddelde (omgepoolde) score op het eerste geldige moment.
    const firstAvg = allPositions.length ? mean(allByPos[allPositions[0]].map((m) => m.avg)) : null

    return {
        noScores, momentCount, deltaTotal, perPersona, anonymous, stateValue, firstAvg, lastRespondents,
        attributedTotal, notAttributedCount, notAttributedReasons, incompleteCount,
    }
}

export default computeMsiEffectValue
